import React from 'react';
import clsx from 'clsx';
import { Plus, Trash2, GalleryHorizontal, Link as LinkIcon, Phone, Reply, Image as ImageIcon, Video } from 'lucide-react';
import { Card, SectionHeader } from '@/components/Card';
import { MediaUpload } from '@/components/MediaUpload';
import { Input } from '@/components/Input';
import { Textarea } from '@/components/Textarea';
import { SearchableSelect } from '@/components/SearchableSelect';
import { Switch } from '@/components/settings/SettingsPage';
import { TemplateCard, TemplateButton, ButtonType } from '@/services/templates.service';

interface Props {
  value: TemplateCard[];
  onChange: (cards: TemplateCard[]) => void;
}

const MAX_CARDS = 10;
const MAX_BUTTONS_PER_CARD = 2;
const BODY_MAX = 160;
const BUTTON_TEXT_MAX = 25;
const URL_MAX = 2048;
const DYNAMIC_SUFFIX = '{{1}}';

const emptyCard = (): TemplateCard => ({ headerType: 'IMAGE', headerImageUrl: '', body: '', buttons: [] });

const BUTTON_TYPES: Array<{ value: ButtonType; label: string; icon: React.ReactNode }> = [
  { value: 'URL', label: 'Website link', icon: <LinkIcon size={13} /> },
  { value: 'PHONE', label: 'Call phone', icon: <Phone size={13} /> },
  { value: 'QUICK_REPLY', label: 'Quick reply', icon: <Reply size={13} /> },
];

const Serial: React.FC<{ n: number; className?: string }> = ({ n, className }) => (
  <span className={clsx('flex h-6 min-w-[24px] shrink-0 items-center justify-center rounded-md bg-primary-soft px-1.5 text-xs font-semibold tabular-nums text-primary', className)}>{n}</span>
);

const Label: React.FC<{ children: React.ReactNode; required?: boolean; right?: React.ReactNode; className?: string }> = ({ children, required, right, className }) => (
  <div className={clsx('mb-1.5 flex items-baseline justify-between gap-3', className)}>
    <span className="text-[13px] font-medium text-text-secondary">{children}{required && <span className="ml-0.5 text-primary">*</span>}</span>
    {right}
  </div>
);

const Counter: React.FC<{ n: number; max: number }> = ({ n, max }) => (
  <span className={clsx('text-2xs tabular-nums', n > max ? 'text-danger' : 'text-text-muted')}>{n.toLocaleString()}/{max.toLocaleString()}</span>
);

/**
 * Carousel cards, side by side: every card has a media header, a short body
 * and 1–2 buttons. Meta wants the same media format and button layout on all
 * cards, so new cards copy the first card's shape.
 */
export const CardsEditor: React.FC<Props> = ({ value, onChange }) => {
  const cards = value ?? [];

  const updateCard = (idx: number, patch: Partial<TemplateCard>) => onChange(cards.map((c, i) => (i === idx ? { ...c, ...patch } : c)));
  const addCard = () => {
    if (cards.length >= MAX_CARDS) return;
    onChange([...cards, { ...emptyCard(), headerType: cards[0]?.headerType ?? 'IMAGE', buttons: (cards[0]?.buttons ?? []).map((b) => ({ ...b, text: b.text, url: b.url, phone: b.phone, payload: b.payload })) }]);
  };
  const removeCard = (idx: number) => onChange(cards.filter((_, i) => i !== idx));
  const updateButton = (cardIdx: number, btnIdx: number, patch: Partial<TemplateButton>) => {
    const next = (cards[cardIdx].buttons ?? []).map((b, i) => (i === btnIdx ? { ...b, ...patch } : b));
    updateCard(cardIdx, { buttons: next });
  };
  const addButton = (cardIdx: number) => {
    const existing = cards[cardIdx].buttons ?? [];
    if (existing.length >= MAX_BUTTONS_PER_CARD) return;
    updateCard(cardIdx, { buttons: [...existing, { type: 'URL', text: '', url: '' }] });
  };
  const removeButton = (cardIdx: number, btnIdx: number) => updateCard(cardIdx, { buttons: (cards[cardIdx].buttons ?? []).filter((_, i) => i !== btnIdx) });

  const remaining = MAX_CARDS - cards.length;

  return (
    <Card padded={false} className="overflow-hidden">
      <div className="border-b border-border px-5 py-4">
        <SectionHeader
          title="Carousel cards"
          subtitle={`${cards.length} of ${MAX_CARDS} cards · at least 2. Every card uses the same media type and button layout.`}
          icon={<GalleryHorizontal size={15} />}
          className="mb-0"
        />
      </div>

      <div className="grid gap-4 p-5 md:grid-cols-2">
        {cards.map((card, i) => {
          const isVideo = card.headerType === 'VIDEO';
          const buttons = card.buttons ?? [];
          return (
            <div key={i} className="flex flex-col gap-4 rounded-2xl border border-border bg-surface-alt/40 p-4">
              <div className="flex items-center gap-2.5">
                <Serial n={i + 1} />
                <span className="text-sm font-semibold text-text-primary">Card {i + 1}</span>
                <button type="button" onClick={() => removeCard(i)} className="ml-auto flex h-8 w-8 items-center justify-center rounded-lg text-text-muted transition-colors hover:bg-danger-soft hover:text-danger" aria-label={`Remove card ${i + 1}`}>
                  <Trash2 size={14} />
                </button>
              </div>

              {/* Header media */}
              <div>
                <Label
                  required
                  right={
                    <span className="inline-flex rounded-lg border border-border bg-surface p-0.5">
                      {([['IMAGE', ImageIcon, 'Image'], ['VIDEO', Video, 'Video']] as const).map(([t, Icon, label]) => (
                        <button
                          key={t}
                          type="button"
                          aria-pressed={card.headerType === t}
                          onClick={() => updateCard(i, { headerType: t, headerImageUrl: t === 'IMAGE' ? card.headerImageUrl : undefined, headerVideoUrl: t === 'VIDEO' ? card.headerVideoUrl : undefined })}
                          className={clsx('flex items-center gap-1 rounded-md px-2 py-1 text-2xs font-semibold transition-colors', card.headerType === t ? 'bg-primary-soft text-primary' : 'text-text-muted hover:text-text-primary')}
                        >
                          <Icon size={12} /> {label}
                        </button>
                      ))}
                    </span>
                  }
                >
                  Header
                </Label>
                <MediaUpload
                  mode={isVideo ? 'video' : 'image'}
                  value={isVideo ? card.headerVideoUrl : card.headerImageUrl}
                  folder={`whatsapp/template/${isVideo ? 'videos' : 'images'}`}
                  onChange={(url) => updateCard(i, isVideo ? { headerVideoUrl: url } : { headerImageUrl: url })}
                />
              </div>

              {/* Body */}
              <div>
                <Label required right={<Counter n={(card.body ?? '').length} max={BODY_MAX} />}>Body text</Label>
                <Textarea
                  rows={3}
                  value={card.body}
                  onChange={(e) => updateCard(i, { body: e.target.value.slice(0, BODY_MAX) })}
                  placeholder="Your message will appear here…"
                />
              </div>

              {/* Buttons */}
              <div>
                <Label
                  required
                  right={buttons.length < MAX_BUTTONS_PER_CARD && (
                    <button type="button" onClick={() => addButton(i)} className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline"><Plus size={12} /> Add button</button>
                  )}
                >
                  Buttons
                </Label>
                {buttons.length === 0 ? (
                  <button type="button" onClick={() => addButton(i)} className="flex w-full items-center justify-center gap-2 rounded-xl border border-dashed border-border bg-surface px-3 py-4 text-sm text-text-muted transition-colors hover:border-primary hover:text-primary">
                    <Plus size={15} /> Add the first button — Meta needs 1–2 per card
                  </button>
                ) : (
                  <div className="space-y-3">
                    {buttons.map((b, bi) => {
                      const dynamic = (b.url ?? '').endsWith(DYNAMIC_SUFFIX);
                      return (
                        <div key={bi} className="space-y-3 rounded-xl border border-border bg-surface p-3.5">
                          <div className="flex items-center gap-2">
                            <Serial n={bi + 1} className="h-5 min-w-[20px] text-2xs" />
                            <span className="whitespace-nowrap text-sm font-medium text-text-primary">Button {bi + 1}</span>
                            <div className="ml-auto min-w-0 flex-1 max-w-[190px]">
                              <SearchableSelect
                                aria-label="Button type"
                                value={b.type}
                                onChange={(v) => updateButton(i, bi, { type: v as ButtonType })}
                                options={BUTTON_TYPES.map((t) => ({ value: t.value, label: t.label, icon: t.icon }))}
                                searchable={false}
                                numbered={false}
                                className="h-8 px-2.5 text-xs"
                              />
                            </div>
                            <button type="button" onClick={() => removeButton(i, bi)} className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-text-muted transition-colors hover:bg-danger-soft hover:text-danger" aria-label={`Remove button ${bi + 1}`}>
                              <Trash2 size={13} />
                            </button>
                          </div>

                          <div>
                            <Label required right={<Counter n={b.text.length} max={BUTTON_TEXT_MAX} />}>Button text</Label>
                            <Input value={b.text} onChange={(e) => updateButton(i, bi, { text: e.target.value.slice(0, BUTTON_TEXT_MAX) })} placeholder="Button text" />
                          </div>

                          {b.type === 'URL' && (
                            <>
                              <div>
                                <Label required right={<Counter n={(b.url ?? '').length} max={URL_MAX} />}>URL</Label>
                                <Input value={b.url ?? ''} onChange={(e) => updateButton(i, bi, { url: e.target.value.slice(0, URL_MAX) })} placeholder="https://example.com" inputMode="url" />
                              </div>
                              <label className="flex items-center gap-3">
                                <Switch
                                  checked={dynamic}
                                  label="URL parameters"
                                  onChange={(on) => {
                                    const base = (b.url ?? '').replace(/\{\{1\}\}$/, '');
                                    updateButton(i, bi, { url: on ? `${base}${base.endsWith('/') || base === '' ? '' : '/'}${DYNAMIC_SUFFIX}` : base });
                                  }}
                                />
                                <span className="min-w-0">
                                  <span className="block text-sm text-text-primary">URL parameters</span>
                                  <span className="block text-2xs text-text-muted">Adds {'{{1}}'} at the end — filled per customer</span>
                                </span>
                              </label>
                            </>
                          )}
                          {b.type === 'PHONE' && (
                            <div>
                              <Label required>Phone number</Label>
                              <Input value={b.phone ?? ''} onChange={(e) => updateButton(i, bi, { phone: e.target.value })} placeholder="+971501234567" inputMode="tel" />
                            </div>
                          )}
                          {b.type === 'QUICK_REPLY' && (
                            <div>
                              <Label>Payload</Label>
                              <Input value={b.payload ?? ''} onChange={(e) => updateButton(i, bi, { payload: e.target.value })} placeholder="Optional — what your bot receives" />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          );
        })}

        {cards.length < MAX_CARDS && (
          <button
            type="button"
            onClick={addCard}
            className="flex min-h-[260px] flex-col items-center justify-center gap-3 rounded-2xl border-2 border-dashed border-border-strong bg-surface-alt/30 p-6 text-center transition-colors hover:border-primary hover:bg-primary-soft/40"
          >
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-primary-soft text-primary"><Plus size={22} /></span>
            <span className="text-sm font-semibold text-text-primary">{cards.length === 0 ? 'Add the first card' : 'Add another card'}</span>
            <span className="text-xs text-text-muted">{cards.length === 0 ? `Carousels need 2–${MAX_CARDS} cards` : `You can add up to ${remaining} more card${remaining === 1 ? '' : 's'}`}</span>
          </button>
        )}
      </div>
    </Card>
  );
};
